import { Icon } from '@iconify/react/dist/iconify.js';
import { useState } from 'react';

const ChildDetails = ({ selectedChild, copiedId, onCopy, onEditChild }) => {
    const [showFullDescription, setShowFullDescription] = useState(false);
    
    if (!selectedChild) return null;

    const toTitleCase = (str) => {
        return str.replace(
            /\w\S*/g,
            (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
    }

    return (
        <div className={`
            child-details-responsive details-container rounded-lg p-4 overflow-y-auto scrollable transition-all duration-500 ease-out
            w-full 2xl:w-3/5 gap-2 flex flex-col
            opacity-100 translate-x-0
        `}>
            <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center">
                <div className="flex flex-row items-center gap-2">
                    {/* <Icon icon="mdi:package-variant" className={`text-3xl ${
                        selectedChild._hasPendingChanges ? 'text-orange-500 opacity-100' :
                        selectedChild._isPendingCreation ? 'text-primary opacity-100' :
                        'text-accent opacity-30'
                    }`} /> */}
                    <div className="flex flex-col gap-2">
                        <h2 className="text-xl font-semibold text-font-primary">
                            {selectedChild.childCategory || 'Unnamed Item'}
                        </h2>
                        {selectedChild._hasPendingChanges && (
                            <div className="text-xs text-orange-600 mt-1">
                                Modified by {selectedChild._changedBy} • {new Date(selectedChild._changedAt).toLocaleDateString()}
                            </div>
                        )}
                        {selectedChild._isPendingCreation && (
                            <div className="text-xs text-blue-600 mt-1">
                                Created by {selectedChild._changedBy} • {new Date(selectedChild._changedAt).toLocaleDateString()}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <div className="flex flex-row items-center gap-2 sm:gap-3 text-font-secondary">
                        <p className="text-xs sm:text-sm">Id: {selectedChild.childEqpId}</p>
                        <button
                            className="text-xs sm:text-sm button-container text-accent p-1.5 sm:p-2 rounded"
                            onClick={() => onCopy(selectedChild.childEqpId)}
                        >
                            <Icon
                                icon={
                                    copiedId === selectedChild.childEqpId
                                        ? 'mdi:check-bold'
                                        : 'solar:copy-linear'
                                }
                                className="text-sm sm:text-lg"
                            />
                        </button>
                    </div>
                    <button 
                        onClick={() => onEditChild(selectedChild)}
                        className="flex flex-row items-center rounded-md gap-1 text-xs sm:text-sm button-container p-1.5 sm:p-2 group hover:bg-accent transition-colors duration-300"
                    >
                        <Icon icon="fluent:box-edit-20-regular" className="text-sm sm:text-lg text-accent group-hover:text-background" />
                        <span className="text-sm font-medium text-font-primary group-hover:text-background">Edit</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:p-2">
                {selectedChild.model && (
                    <div className="flex flex-col items-start gap-1 card-container rounded-md p-2">
                        <span className="text-font-primary text-sm">Model</span>
                        <span className="text-font-primary font-semibold">{selectedChild.model}</span>
                    </div>
                )}
                {selectedChild.brand && (
                    <div className="flex flex-col items-start gap-1 card-container rounded-md p-2">
                        <span className="text-font-primary text-sm">Brand</span>
                        <span className="text-font-primary font-semibold">{selectedChild.brand}</span>
                    </div>
                )}

                {/* Common Synonyms */}
                <div className="flex flex-col items-start gap-1 col-span-2 card-container rounded-md p-2">
                    <span className="text-font-primary text-sm">Synonyms</span>
                    <div className="flex flex-wrap gap-2 font-semibold">
                        {selectedChild.commonSynonyms}
                    </div>
                </div>

                {/* Key Selling Points */}
                <div className="flex flex-col items-start gap-1 col-span-2 card-container rounded-md p-2">
                    <span className="text-font-primary text-sm">Key Selling Points</span>
                    <span className="text-font-primary font-semibold">{selectedChild.keySellingPoints || 'No key selling points available'}</span>
                </div>

                {/* Pricing */}
                <div className="flex flex-col items-start gap-1 col-span-2">
                    <span className="text-font-primary text-sm">Pricing</span>
                    <div className="grid grid-cols-2 gap-2 w-full">
                        {selectedChild.pricing ? (
                            (() => {
                                try {
                                    const pricingData = JSON.parse(selectedChild.pricing);
                                    return Object.entries(pricingData).map(([duration, price], index) => (
                                        <span key={index} className="w-full flex flex-row items-center justify-between text-sm text-font-primary p-2 card-container rounded-md">
                                            <span className="text-font-primary">{duration}:</span>
                                            <span className="text-accent font-semibold">{price}</span>
                                        </span>
                                    ));
                                } catch (error) {
                                    return <span className="text-font-secondary">Invalid pricing format</span>;
                                }
                            })()
                        ) : (
                            <span className="text-font-secondary">No pricing available</span>
                        )}
                    </div>
                </div>

                {/* Description */}
                <div className="flex flex-col items-start gap-1 col-span-2">
                    <span className="text-font-primary text-sm">Description</span>
                    {(() => {
                        const description = selectedChild.description || 'No description available';
                        const words = description.split(' ');
                        const shouldTruncate = words.length > 50;
                        const displayText = shouldTruncate && !showFullDescription 
                            ? words.slice(0, 50).join(' ') + '...'
                            : description;
                        
                        return (
                            <div className="flex flex-col gap-2">
                                <span className="text-font-secondary">{displayText}</span>
                                {shouldTruncate && (
                                    <button
                                        onClick={() => setShowFullDescription(!showFullDescription)}
                                        className="text-accent hover:text-primary text-sm font-medium transition-colors duration-200 self-start"
                                    >
                                        {showFullDescription ? 'See less' : 'See more'}
                                    </button>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

export default ChildDetails;
